// src/components/MarkPaidModal.jsx
// Record a tenant payment — updates balance, payment history, and outreach log
import React, { useState } from 'react';
import { formatCurrency } from '../utils/formatters';

export default function MarkPaidModal({ tenant, propertyId, onClose, onRecorded }) {
  const balance = tenant?.currentBalance || 0;

  const [amount, setAmount]   = useState(balance.toFixed(2));
  const [date, setDate]       = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const parsedAmount = parseFloat(amount) || 0;
  const isValid = parsedAmount > 0 && parsedAmount <= balance + 0.01;

  const handleRecord = async () => {
    if (!isValid) return;
    setSaving(true);
    setError('');
    try {
      // onRecorded callback does the actual Firestore write via useTenants.recordPayment
      await onRecorded({ amount: parsedAmount, date, note });
      onClose();
    } catch (err) {
      setError('Failed to record payment: ' + err.message);
      setSaving(false);
    }
  };

  if (!tenant) return null;

  const newBalance = Math.max(balance - parsedAmount, 0);
  const isFullPayment = parsedAmount >= balance - 0.01;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">

        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">✅ Record Payment</h2>
            <p className="text-sm text-gray-500 mt-0.5">{tenant.name} · Unit {tenant.unit}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        <div className="p-6 space-y-5">

          {/* Current balance */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-red-500 font-semibold uppercase tracking-wide">Current Balance</p>
              <p className="text-xl font-bold text-red-600 mt-0.5">{formatCurrency(balance)}</p>
            </div>
            {tenant.currentDaysLate > 0 && (
              <span className="text-sm text-red-400">{tenant.currentDaysLate}d overdue</span>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Amount Received</label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 font-medium">$</span>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                min={0}
                max={balance}
                step="0.01"
                className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
              />
            </div>
            {/* Quick-fill buttons */}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setAmount(balance.toFixed(2))}
                className="text-xs text-tenafi-green hover:underline font-medium"
              >
                Full balance ({formatCurrency(balance)})
              </button>
              {balance > 0 && (
                <button
                  type="button"
                  onClick={() => setAmount((balance / 2).toFixed(2))}
                  className="text-xs text-tenafi-green hover:underline font-medium"
                >
                  Half ({formatCurrency(balance / 2)})
                </button>
              )}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Date</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. Paid by check, money order, online..."
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
            />
          </div>

          {/* Balance preview */}
          {parsedAmount > 0 && (
            <div className={`rounded-xl border p-4 ${isFullPayment ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isFullPayment ? 'text-green-600' : 'text-amber-600'}`}>
                {isFullPayment ? '✅ Full payment — balance cleared' : '⚠️ Partial payment'}
              </p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">New balance after payment:</span>
                <span className={`font-bold ${isFullPayment ? 'text-green-700' : 'text-amber-700'}`}>
                  {isFullPayment ? '$0.00' : formatCurrency(newBalance)}
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 flex items-center justify-end gap-3">
          <button onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button
            onClick={handleRecord}
            disabled={saving || !isValid}
            className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Recording...</>
            ) : (
              '✓ Record Payment'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
